import {cva, type VariantProps} from 'class-variance-authority';

import {AriaRole, forwardRef, ReactNode} from 'react';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ResponsiveValue, isResponsiveValue} from '../../types/ResponsiveValue';
import {cn} from '../../utils/cn';
import {getResponsiveClassesWithoutPrefix} from '../../utils/getResponsiveClasses';

/**
 * Text-specific color paths from the theme
 */
export type ThemeTextColorPath =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'informational'
  | 'success'
  | 'warning'
  | 'danger'
  | 'link'
  | 'white';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextSize = '2xlarge' | 'large' | 'base' | 'small' | 'xSmall' | 'xxSmall';

const TEXT_SIZE_CLASSES: Record<TextSize, string> = {
  '2xlarge': 'text-[length:var(--text-size-2xlarge)] leading-[var(--text-leading-2xlarge)]',
  large: 'text-[length:var(--text-size-large)] leading-[var(--text-leading-large)]',
  base: 'text-[length:var(--text-size-base)] leading-[var(--text-leading-base)]',
  small: 'text-[length:var(--text-size-small)] leading-[var(--text-leading-small)]',
  xSmall: 'text-[length:var(--text-size-x-small)] leading-[var(--text-leading-x-small)]',
  xxSmall: 'text-[length:var(--text-size-xx-small)] leading-[var(--text-leading-xx-small)]',
};

const TEXT_COLOR_CLASSES: Record<ThemeTextColorPath, string> = {
  primary: 'text-[var(--color-primary)]',
  secondary: 'text-[var(--color-secondary)]',
  tertiary: 'text-[var(--color-tertiary)]',
  informational: 'text-[var(--color-informational)]',
  success: 'text-[var(--color-success)]',
  warning: 'text-[var(--color-warning)]',
  danger: 'text-[var(--color-danger)]',
  link: 'text-[var(--color-link)]',
  white: 'text-[var(--color-white)]',
};

const TEXT_ALIGN_CLASSES: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const textVariants = cva('font-body tracking-[0]', {
  variants: {
    weight: {
      default: 'font-[var(--text-weight-default)]',
      alternative: 'font-[var(--text-weight-alternative)]',
    },
    display: {
      block: 'block',
      inline: 'inline',
    },
    strikethrough: {
      true: 'line-through',
      false: 'no-underline',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
    whiteSpace: {
      normal: 'whitespace-normal',
      nowrap: 'whitespace-nowrap',
      preWrap: 'whitespace-pre-wrap',
    },
    overflowWrap: {
      anywhere: 'break-all',
      normal: '',
    },
  },
  defaultVariants: {
    weight: 'default',
    display: 'block',
    strikethrough: false,
    truncate: false,
    whiteSpace: 'normal',
    overflowWrap: 'normal',
  },
});

export interface TextProps extends TestIdProps, VariantProps<typeof textVariants> {
  size?: TextSize | ResponsiveValue<TextSize>;
  color?: ThemeTextColorPath | Nullish;
  inline?: true;
  alternative?: boolean | Nullish;
  strikethrough?: boolean | Nullish;
  children?: string | ReactNode | Nullish;
  noWrap?: boolean;
  preWrap?: boolean;
  role?: AriaRole;
  overflowWrap?: 'anywhere';
  title?: string;
  align?: TextAlign | ResponsiveValue<TextAlign>;
  id?: string;
}

/**
 * Resolves size prop to Tailwind classes (supports responsive values)
 */
const getSizeClasses = (size: TextSize | ResponsiveValue<TextSize> | undefined): string => {
  const defaultSize: TextSize = 'base';

  if (size === undefined) {
    return TEXT_SIZE_CLASSES[defaultSize];
  }

  if (!isResponsiveValue(size)) {
    return TEXT_SIZE_CLASSES[size];
  }

  const responsiveClasses = Object.entries(size).reduce((acc, [breakpoint, sizeValue]) => {
    if (sizeValue === undefined) {
      return acc;
    }
    return {
      ...acc,
      [breakpoint]: TEXT_SIZE_CLASSES[sizeValue],
    };
  }, {} as ResponsiveValue<string>);

  return getResponsiveClassesWithoutPrefix(responsiveClasses);
};

/**
 * Resolves align prop to Tailwind classes (supports responsive values)
 */
const getAlignClasses = (align: TextAlign | ResponsiveValue<TextAlign> | undefined): string => {
  if (align === undefined) {
    return '';
  }

  if (!isResponsiveValue(align)) {
    return TEXT_ALIGN_CLASSES[align];
  }

  const responsiveClasses = Object.entries(align).reduce((acc, [breakpoint, alignValue]) => {
    if (alignValue === undefined) {
      return acc;
    }
    return {
      ...acc,
      [breakpoint]: TEXT_ALIGN_CLASSES[alignValue],
    };
  }, {} as ResponsiveValue<string>);

  return getResponsiveClassesWithoutPrefix(responsiveClasses);
};

/**
 * Resolves white-space handling based on noWrap and preWrap props
 */
const getWhiteSpaceVariant = (
  noWrap: boolean | undefined,
  preWrap: boolean | undefined
): 'normal' | 'nowrap' | 'preWrap' => {
  if (noWrap) {
    return 'nowrap';
  }
  if (preWrap) {
    return 'preWrap';
  }
  return 'normal';
};

/**
 * Text component - renders text with consistent typography
 * @example
 * <Text size="large" color="primary">Hello World</Text>
 * <Text size={{ base: 'small', lg: 'base' }} alternative>Responsive text</Text>
 */
export const Text = forwardRef<HTMLDivElement, TextProps>((props, ref) => {
  const colorClass = TEXT_COLOR_CLASSES[props.color ?? 'primary'];
  const sizeClasses = getSizeClasses(props.size);
  const alignClasses = getAlignClasses(props.align);

  const className = cn(
    textVariants({
      weight: props.alternative ? 'alternative' : 'default',
      display: props.inline ? 'inline' : 'block',
      strikethrough: Boolean(props.strikethrough),
      truncate: Boolean(props.noWrap),
      whiteSpace: getWhiteSpaceVariant(props.noWrap, props.preWrap),
      overflowWrap: props.overflowWrap === 'anywhere' ? 'anywhere' : 'normal',
    }),
    colorClass,
    sizeClasses,
    alignClasses
  );

  return (
    <div
      ref={ref}
      id={props.id}
      data-testid={suffixTestId('text', props)}
      role={props.role}
      className={className}
      title={props.title}
    >
      {props.children}
    </div>
  );
});
